// SPDX-License-Identifier: AGPL-3.0-or-later
import {useCallback, useEffect, useRef, useState} from 'react';

interface PreviewSource {
	url: string;
	dispose?: () => void;
}

/** Changing voices or leaving the tab also cancels a preview that is still loading. */
export function useVoicePreview(load: () => Promise<PreviewSource>) {
	const [status, setStatus] = useState<'loading' | 'playing' | null>(null);
	const [failed, setFailed] = useState(false);
	const generation = useRef(0);
	const current = useRef<{player: HTMLAudioElement; dispose?: () => void} | null>(null);
	const release = useCallback(() => {
		generation.current++;
		const audio = current.current;
		current.current = null;
		if (audio) {
			audio.player.onended = null;
			audio.player.onerror = null;
			audio.player.pause();
			audio.player.removeAttribute('src');
			audio.player.load();
			audio.dispose?.();
		}
	}, []);
	const stop = useCallback(() => {
		release();
		setStatus(null);
	}, [release]);
	useEffect(() => release, [release]);
	const preview = async () => {
		if (status) {
			stop();
			return;
		}
		setFailed(false);
		setStatus('loading');
		const attempt = ++generation.current;
		try {
			const source = await load();
			if (attempt !== generation.current) {
				source.dispose?.();
				return;
			}
			const player = new Audio(source.url);
			current.current = {player, dispose: source.dispose};
			player.onended = stop;
			player.onerror = () => {
				if (attempt === generation.current) {
					stop();
					setFailed(true);
				}
			};
			await player.play();
			if (attempt === generation.current) setStatus('playing');
		} catch {
			if (attempt === generation.current) {
				stop();
				setFailed(true);
			}
		}
	};
	return {status, failed, preview, stop};
}
